// GoHighLevel SSO handshake.
//
// When a Marketplace App Custom Page is loaded in the GHL iframe, the page
// asks the parent for the current user/location context via a postMessage:
//
//   window.parent.postMessage({ message: 'REQUEST_USER_DATA' }, '*')
//
// The parent (GHL) replies with:
//
//   { message: 'REQUEST_USER_DATA_RESPONSE', payload: <encrypted-or-plain-blob> }
//
// If the app is configured with an SSO key, the payload is an AES-encrypted
// blob that only the backend (which holds the SSO key) can decrypt and trust.
// The frontend treats the payload as opaque and forwards it to the backend.
//
// NOTE: the GHL SSO payload does NOT include the currently-open contact id —
// only user/company/location. The contact is selected via the in-app picker
// or supplied via a `?contactId=` deep-link.

import type { GhlContext } from '../types';

let cached: GhlContext | null = null;
let inflight: Promise<GhlContext> | null = null;
// locationId (+ any user params) from the Custom Page URL. Used ONLY as a
// fallback if the postMessage handshake times out -- we always try the handshake
// first to obtain the ENCRYPTED SSO blob, which the backend decrypts for a
// trusted identity (per-rep owner/sender + SSO enforcement).
let urlFallback: GhlContext | null = null;

const REQUEST = 'REQUEST_USER_DATA';
const RESPONSE = 'REQUEST_USER_DATA_RESPONSE';
const TIMEOUT_MS = 6000;
const RETRY_MS = 500;

// Only trust SSO responses coming from GoHighLevel parent frames.
const ALLOWED_HOSTS = ['gohighlevel.com', 'leadconnectorhq.com'];
function isAllowedOrigin(origin: string): boolean {
  try {
    const u = new URL(origin);
    if (u.protocol !== 'https:') return false;
    return ALLOWED_HOSTS.some((h) => u.hostname === h || u.hostname.endsWith(`.${h}`));
  } catch {
    return false;
  }
}

export function getGhlContext(force = false): Promise<GhlContext> {
  // `force` busts the cache and re-runs the postMessage handshake. Used by the
  // API client on a 401: under SSO enforcement a request without a decryptable
  // blob is rejected, so if we ever cached a no-blob fallback (rare handshake
  // timeout), a 401 triggers a fresh handshake -- by now the GHL parent is
  // definitely listening -- and the retried request carries the real blob.
  if (force) {
    cached = null;
    inflight = null;
  }
  if (cached) return Promise.resolve(cached);
  if (inflight) return inflight;

  inflight = new Promise<GhlContext>((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let retry: ReturnType<typeof setInterval> | undefined;

    const cleanup = () => {
      window.removeEventListener('message', onMessage);
      if (timer) clearTimeout(timer);
      if (retry) clearInterval(retry);
    };

    const onMessage = (ev: MessageEvent) => {
      // Reject messages from any frame that isn't GoHighLevel.
      if (!isAllowedOrigin(ev.origin)) return;

      const data = ev.data as { message?: string; payload?: unknown } | undefined;
      if (!data || data.message !== RESPONSE) return;

      cleanup();

      // Payload shape depends on Marketplace App configuration.
      // - If SSO key is set, payload is an encrypted string (backend decrypts).
      // - Otherwise it's a plain object with user data.
      const payload = data.payload as GhlContext | string;
      const ctx: GhlContext =
        typeof payload === 'string'
          ? ({
              encrypted: payload,
              userId: '',
              companyId: '',
              type: 'location',
              role: '',
              email: '',
              locationId: urlFallback?.locationId,
            } as GhlContext)
          : ({ ...payload, locationId: (payload as GhlContext).locationId || urlFallback?.locationId } as GhlContext);

      console.info('[pdc] SSO handshake ok', {
        encrypted: typeof payload === 'string',
        locationId: ctx.locationId,
      });
      cached = ctx;
      resolve(ctx);
    };

    window.addEventListener('message', onMessage);

    // Ask immediately, then keep asking on an interval — when the iframe first
    // loads, the GHL parent frame may not be listening yet, so a single request
    // can be missed (the classic "SSO timeout"). Retrying until the response
    // arrives (or we time out) makes the handshake reliable.
    const ask = () => window.parent.postMessage({ message: REQUEST }, '*');
    ask();
    retry = setInterval(ask, RETRY_MS);

    timer = setTimeout(() => {
      cleanup();
      if (urlFallback) {
        // In GHL and the parent never replied (rare) -- fall back to the URL
        // locationId so the panel still works (grace mode; no SSO blob this load).
        console.warn('[pdc] SSO handshake timed out; using URL locationId fallback (no blob)');
        cached = urlFallback;
        resolve(urlFallback);
      } else {
        inflight = null;
        reject(new Error('GHL SSO timeout — is this page loaded inside the GHL iframe?'));
      }
    }, TIMEOUT_MS);
  });

  return inflight;
}

/**
 * Seeds the cached GHL context from the URL or a dev stub, so `getGhlContext()`
 * resolves without the postMessage handshake. Called once in main.tsx before App.
 *
 * Two cases:
 *  - `?ghlStub=1` (or `window.__GHL_STUB__`) — local dev outside GHL. DEV-ONLY:
 *    gated on `import.meta.env.DEV` so a production build never honours it (a
 *    stub context has no SSO blob and would just be a 401 under enforcement).
 *  - `?locationId=…` — the location is supplied directly in the URL. Used by the
 *    Custom Page URL (`…?locationId={{location.id}}`) and by per-contact
 *    deep-link buttons (`…?contactId=…&locationId={{location.id}}`). Lets us
 *    skip SSO entirely when the location is known from the URL.
 */
export function setupSsoStub() {
  const params = new URLSearchParams(window.location.search);

  const wantStub =
    import.meta.env.DEV &&
    (params.get('ghlStub') === '1' ||
      (window as unknown as { __GHL_STUB__?: boolean }).__GHL_STUB__);
  if (wantStub) {
    cached = {
      userId: 'stub-user',
      companyId: 'stub-company',
      locationId: 'stub-location',
      type: 'location',
      role: 'admin',
      email: 'stub@miamifirstremodeling.com',
      firstName: 'Stub',
      lastName: 'User',
    };
    return;
  }

  // Guard against GHL not substituting the merge field — if the param still
  // contains `{{…}}`, ignore it and fall through to the SSO handshake.
  const locationId = params.get('locationId');
  if (locationId && !locationId.includes('{')) {
    // Fallback ONLY -- getGhlContext still runs the handshake to fetch the SSO
    // blob. (Previously this seeded `cached` and short-circuited the handshake,
    // so the rep's identity/blob never reached the backend -- audit finding H4.)
    urlFallback = {
      userId: params.get('userId') || '',
      companyId: params.get('companyId') || '',
      locationId,
      type: 'location',
      role: '',
      email: params.get('email') || params.get('userEmail') || '',
    };
  }
}
